import { Link } from "react-router-dom";
import { ArrowRight, Globe2, TrendingUp, Sparkles, LifeBuoy, CheckCircle2 } from "lucide-react";

const VALUES = [
  { title: "Craft over speed", body: "We move fast without cutting corners. Details compound." },
  { title: "Client outcomes first", body: "Deliverables are outputs. We're measured on your business outcomes." },
  { title: "Radical transparency", body: "Weekly updates, open project boards, honest conversations — always." },
  { title: "Long-term partnership", body: "We're most proud of clients who've been with us for years." },
];

const TEAM = [
  { name: "Amelia Patel", role: "Creative Director", img: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=500&fit=crop" },
  { name: "David Okafor", role: "Head of Engineering", img: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=400&h=500&fit=crop" },
  { name: "Sofia Ramirez", role: "Growth Lead", img: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&h=500&fit=crop" },
  { name: "Kenji Nakamura", role: "Principal Designer", img: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=500&fit=crop" },
];

const PILLARS = [
  {
    n: "01",
    icon: Globe2,
    title: "Web Strategy & Development",
    intro: "Your website is your digital storefront. We build secure, scalable, and high-performing platforms tailored to your business goals.",
    color: { bg: "bg-blue-100", ring: "ring-blue-200", text: "text-blue-600" },
    items: [
      { name: "Website Design", body: "Crafting visually stunning, user-centric UI/UX designs that capture your brand identity and captivate visitors." },
      { name: "Website Development", body: "Transforming designs into clean, responsive, and fast-loading websites optimized for all devices." },
      { name: "E-Commerce Websites", body: "Building robust online stores with seamless payment gateways, inventory management, and smooth checkout experiences to maximize sales." },
      { name: "Web Portals", body: "Developing custom, secure, and interactive multi-user platforms tailored for enterprises, communities, or specific industry needs." },
    ],
  },
  {
    n: "02",
    icon: TrendingUp,
    title: "Digital Growth & Marketing",
    intro: "A great website needs the right audience. We help you get noticed by the right people at the right time.",
    color: { bg: "bg-orange-100", ring: "ring-orange-200", text: "text-orange-600" },
    items: [
      { name: "Digital Marketing", body: "Deploying data-driven strategies—including SEO, PPC, and social media marketing—to boost your online visibility and drive high-converting traffic." },
      { name: "Content Writing", body: "Crafting compelling, SEO-friendly copy, blogs, and marketing text that tells your brand story and engages your audience." },
    ],
  },
  {
    n: "03",
    icon: Sparkles,
    title: "Brand Identity",
    intro: "Foundational visuals that anchor how the world sees your business.",
    color: { bg: "bg-amber-100", ring: "ring-amber-200", text: "text-amber-600" },
    items: [
      { name: "Logo Design", body: "Creating memorable, unique, and impactful logos that serve as the foundational anchor for your brand's visual identity." },
    ],
  },
  {
    n: "04",
    icon: LifeBuoy,
    title: "Continuous Support",
    intro: "We stay in your corner long after launch.",
    color: { bg: "bg-emerald-100", ring: "ring-emerald-200", text: "text-emerald-600" },
    items: [
      { name: "Website Maintenance", body: "Providing ongoing technical support, security updates, backups, and performance optimization so your digital assets remain secure and up-to-date while you focus on running your business." },
    ],
  },
];

export default function About() {
  return (
    <div>
      {/* Hero */}
      <section className="border-b border-brand-border">
        <div className="container-x pt-20 pb-16 lg:pt-28 lg:pb-20">
          <div className="max-w-3xl">
            <div className="eyebrow mb-3">About TelstraNics</div>
            <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tight" data-testid="about-title">A studio for builders who care about what they ship.</h1>
            <p className="mt-6 text-lg text-brand-muted leading-relaxed">
              At Telstranics, we are a full-service digital agency dedicated to helping businesses establish, grow, and maintain a powerful online presence. We blend strategic creativity with cutting-edge technology to deliver end-to-end digital solutions that drive results, engage audiences, and scale operations.
            </p>
            <p className="mt-4 text-lg text-brand-muted leading-relaxed">
              From first impressions to long-term growth, we handle every aspect of your brand's digital journey.
            </p>
          </div>
        </div>
      </section>

      {/* Story with image */}
      <section className="container-x py-20 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <img
              src="https://images.unsplash.com/photo-1603201667230-bd139210db18?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
              alt="TelstraNics team collaborating"
              className="rounded-3xl w-full h-full object-cover aspect-[4/5]"
            />
          </div>
          <div className="lg:col-span-7">
            <div className="eyebrow mb-3">Our Approach</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">One team. Every layer of your digital presence.</h2>
            <div className="mt-6 space-y-4 text-brand-muted leading-relaxed">
              <p>We started TelstraNics because agencies felt broken — layers of account managers, revolving-door juniors, and cookie-cutter deliverables. We built the opposite: senior-only teams, direct client access, and a bias for outcomes.</p>
              <p>Nearly a decade later, we've grown into a distributed studio of designers, engineers, and marketers — but our operating model is the same: small pods, senior talent, direct communication.</p>
              <p>We've helped launch fintech products, scaled e-commerce brands past nine figures, rebuilt SaaS marketing sites, and produced editorial content that ranks. But mostly, we've built lasting partnerships.</p>
            </div>
            <Link to="/contact" data-testid="about-cta" className="btn-primary mt-8">Work With Us <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* Four pillars */}
      <section className="bg-brand-surface border-y border-brand-border">
        <div className="container-x py-20 lg:py-24">
          <div className="max-w-2xl mb-14">
            <div className="eyebrow mb-3">Our Core Services</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">Four pillars. One unified strategy.</h2>
            <p className="mt-4 text-brand-muted leading-relaxed">We break our expertise into four primary pillars to ensure your business thrives online.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {PILLARS.map((p) => (
              <div key={p.title} data-testid={`about-pillar-${p.n}`} className="bg-white border border-brand-border rounded-2xl p-8">
                <div className="flex items-start gap-4 mb-6">
                  <div className={`w-14 h-14 rounded-2xl ${p.color.bg} ring-1 ${p.color.ring} flex items-center justify-center shrink-0`}>
                    <p.icon className={`w-7 h-7 ${p.color.text}`} strokeWidth={1.75} />
                  </div>
                  <div>
                    <div className="font-mono text-xs text-brand-muted mb-1">{p.n}</div>
                    <h3 className="font-display font-bold text-2xl text-brand-ink leading-tight">{p.title}</h3>
                  </div>
                </div>
                <p className="text-brand-muted leading-relaxed mb-6">{p.intro}</p>
                <ul className="space-y-4">
                  {p.items.map((it) => (
                    <li key={it.name} className="flex items-start gap-3">
                      <CheckCircle2 className={`w-5 h-5 mt-0.5 shrink-0 ${p.color.text}`} />
                      <div>
                        <div className="font-medium text-brand-ink">{it.name}</div>
                        <div className="text-sm text-brand-muted mt-0.5 leading-relaxed">{it.body}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="container-x py-20 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-3">Why Choose Us</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">The full-suite advantage.</h2>
            <p className="mt-6 text-brand-muted leading-relaxed">
              Instead of juggling multiple agencies for design, coding, writing, and marketing, we provide a unified, cohesive strategy under one roof.
            </p>
            <p className="mt-4 text-brand-muted leading-relaxed">
              We don't just build websites — we build growth engines. Whether you're a startup launching your first logo and website, or an established enterprise upgrading to a complex web portal and scaling digital marketing, Telstranics is your trusted partner for digital excellence.
            </p>
            <Link to="/contact" data-testid="about-why-cta" className="btn-primary mt-8">Start a Project <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {VALUES.map((v, i) => (
              <div key={v.title} className="p-6 bg-white border border-brand-border rounded-2xl">
                <div className="font-mono text-xs text-brand-blue mb-3">0{i + 1}</div>
                <div className="font-display font-bold text-lg text-brand-ink">{v.title}</div>
                <div className="mt-2 text-sm text-brand-muted leading-relaxed">{v.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="bg-brand-surface border-t border-brand-border">
        <div className="container-x py-20 lg:py-24">
          <div className="max-w-2xl mb-12">
            <div className="eyebrow mb-3">The Team</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">People, not resources</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {TEAM.map((p) => (
              <div key={p.name} className="group">
                <div className="aspect-[4/5] overflow-hidden rounded-2xl bg-white">
                  <img src={p.img} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="mt-4">
                  <div className="font-display font-bold text-brand-ink">{p.name}</div>
                  <div className="text-sm text-brand-muted">{p.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
